import { Link, useLocation, useNavigate } from "react-router-dom";
import { Trophy, History as HistoryIcon, BarChart3, Plus, Users, LogIn, LogOut, ShieldCheck, Activity, Eye, ArrowLeft, Menu, X, Shield } from "lucide-react";
import OfflineIndicator from "@/components/OfflineIndicator";
import { useAuth } from "@/lib/auth";
import { useEffect, useState } from "react";

const NavLink = ({ to, label, icon: Icon, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase()}`}
    className={`group flex items-center gap-2 px-3 py-2 font-display uppercase tracking-[0.18em] text-xs font-bold transition-all border-b-2 ${
      active
        ? "border-primary text-primary"
        : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
    }`}
  >
    <Icon className="w-4 h-4" strokeWidth={2.5} />
    <span>{label}</span>
  </Link>
);

function RoleBadge({ role }) {
  const RoleIcon = role === "admin" ? ShieldCheck : role === "scorer" ? Activity : Eye;
  const cls = role === "admin"
    ? "border-primary text-primary"
    : role === "scorer"
    ? "border-foreground text-foreground"
    : "border-muted-foreground text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${cls} text-[9px] uppercase tracking-[0.2em] font-bold`}>
      <RoleIcon className="w-3 h-3" /> {role}
    </span>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onExpired = () => nav("/login", { replace: true });
    window.addEventListener("crickpad-auth-expired", onExpired);
    return () => window.removeEventListener("crickpad-auth-expired", onExpired);
  }, [nav]);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const isAdmin = user?.role === "admin";
  const canScore = user?.role === "admin" || user?.role === "scorer";

  // Hide Back button on root, auth pages, and primary destinations from the menu
  const hideBackOn = ["/", "/login", "/register"];
  const showBack = !hideBackOn.includes(pathname);

  // Build nav items (filtered by role/auth)
  const navItems = [
    { to: "/", label: "Home", icon: Trophy, show: true },
    { to: "/history", label: "History", icon: HistoryIcon, show: true },
    { to: "/teams", label: "Teams", icon: Shield, show: canScore },
    { to: "/stats", label: "Stats", icon: BarChart3, show: !!user },
    { to: "/admin/users", label: "Users", icon: Users, show: isAdmin },
  ].filter((i) => i.show);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-3 sm:px-8 h-16 gap-2 sm:gap-3">
          {/* Left cluster: Back + Logo */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {showBack && (
              <button
                onClick={() => nav(-1)}
                data-testid="back-btn"
                aria-label="Back"
                className="shrink-0 tactical-btn border border-border hover:border-primary hover:text-primary flex items-center justify-center w-9 h-9"
                title="Back"
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={3} />
              </button>
            )}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="logo-link">
              <div className="relative shrink-0">
                <div className="w-9 h-9 bg-primary text-primary-foreground flex items-center justify-center font-display font-black text-lg">
                  C
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-destructive border-2 border-background"></div>
              </div>
              <div className="hidden xs:flex sm:flex flex-col leading-none min-w-0">
                <span className="font-display font-black text-base sm:text-lg uppercase tracking-tight truncate">CrickPad</span>
                <span className="hidden sm:inline text-[9px] uppercase tracking-[0.3em] text-muted-foreground">Live Scoring</span>
              </div>
            </Link>
          </div>

          {/* Center cluster: Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="top-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                active={pathname === item.to}
              />
            ))}
          </nav>

          {/* Right cluster: User actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            {user ? (
              <>
                <div className="hidden lg:flex flex-col items-end leading-none">
                  <Link to="/change-password" className="font-display font-bold text-xs uppercase tracking-tight hover:text-primary" data-testid="user-name">
                    {user.name}
                  </Link>
                  <span className="mt-0.5"><RoleBadge role={user.role} /></span>
                </div>
                {canScore && (
                  <Link
                    to="/new"
                    data-testid="new-match-header-btn"
                    className="tactical-btn bg-primary text-primary-foreground hover:bg-[#C9E620] flex items-center gap-2 px-3 sm:px-4 py-2 text-xs"
                  >
                    <Plus className="w-4 h-4" strokeWidth={3} />
                    <span className="hidden sm:inline">New Match</span>
                  </Link>
                )}
                <button
                  onClick={() => { logout(); nav("/login"); }}
                  data-testid="logout-btn"
                  className="tactical-btn border border-border hover:border-destructive hover:text-destructive flex items-center gap-1.5 px-3 py-2 text-xs"
                  title="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5" strokeWidth={3} />
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="tactical-btn bg-primary text-primary-foreground hover:bg-[#C9E620] flex items-center gap-2 px-3 sm:px-4 py-2 text-xs"
              >
                <LogIn className="w-4 h-4" strokeWidth={3} />
                <span className="hidden sm:inline">Sign In</span>
              </Link>
            )}
            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              className="md:hidden tactical-btn border border-border hover:border-primary flex items-center justify-center w-9 h-9"
            >
              {mobileOpen ? <X className="w-4 h-4" strokeWidth={3} /> : <Menu className="w-4 h-4" strokeWidth={3} />}
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileOpen && (
          <nav
            data-testid="mobile-nav"
            className="md:hidden border-t border-border bg-background/95 backdrop-blur-sm"
          >
            <div className="max-w-7xl mx-auto px-3 py-2 flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  active={pathname === item.to}
                  onClick={() => setMobileOpen(false)}
                />
              ))}
              {user && (
                <Link
                  to="/change-password"
                  onClick={() => setMobileOpen(false)}
                  data-testid="nav-account"
                  className="flex items-center justify-between px-3 py-2 border-t border-border mt-1"
                >
                  <span className="font-display uppercase tracking-[0.18em] text-xs font-bold text-muted-foreground">{user.name}</span>
                  <RoleBadge role={user.role} />
                </Link>
              )}
            </div>
          </nav>
        )}

        <div className="max-w-7xl mx-auto px-3 sm:px-8 pb-2 flex justify-end">
          <OfflineIndicator />
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 flex flex-col sm:flex-row justify-between items-center text-xs uppercase tracking-[0.2em] text-muted-foreground gap-2">
          <span>CrickPad © 2026 — Built for Backyard Legends</span>
          <span className="font-mono">v1.0.0</span>
        </div>
      </footer>
    </div>
  );
}
